/**
 * Section C Page - C.計入期說明
 * Start date, crediting period type and duration
 * UI 模式：單頁捲動表單
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import RichTextEditor from '../components/RichTextEditor';
import DateInput from '../components/DateInput';
import { requestTransactionToken } from '../services/transactionService';
import fileAttachmentsService from '../services/fileAttachmentsService';
import sectionCService, { SectionCDetail } from '../services/sectionCService';
import FunctionPageHeader from '../components/FunctionPageHeader';

// ── 儲存回饋 ──────────────────────────────────────────────────────
function useSaveFeedback() {
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const show = (ok: boolean, text: string) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 2500);
  };
  return { msg, show };
}

// ── 日期區間計算 ───────────────────────────────────────────────────
function calcEndDate(startDDMMYYYY: string, years: number): string {
  const parts = startDDMMYYYY.split('/');
  if (parts.length !== 3) return '';
  const [dd, mm, yyyy] = parts.map(Number);
  if (!dd || !mm || !yyyy) return '';
  const end = new Date(yyyy + years, mm - 1, dd - 1);
  const ed = String(end.getDate()).padStart(2, '0');
  const em = String(end.getMonth() + 1).padStart(2, '0');
  return `${ed}/${em}/${end.getFullYear()}`;
}

// ════════════════════════════════════════════════════════════════
// SectionCPage
// ════════════════════════════════════════════════════════════════
const SectionCPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { selectedProjectId } = useProject();
  const { hasPermission } = usePermission();
  const canUpdate = hasPermission('section_c', 'update');
  const { msg, show } = useSaveFeedback();

  const [detail, setDetail] = useState<SectionCDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);

  // ── 表單狀態 ───────────────────────────────────────────────────
  const [projectStartDate,       setProjectStartDate]       = useState('');
  const [projectDescript,        setProjectDescript]        = useState('');
  const [operationalLifetime,    setOperationalLifetime]    = useState<string>('');
  const [creditingPeriodType,    setCreditingPeriodType]    = useState<number | null>(null);
  const [creditingPeriodStart,   setCreditingPeriodStart]   = useState('');
  const [creditingPeriodDuration,setCreditingPeriodDuration]= useState<string>('');
  const [fileAttachmentsId,      setFileAttachmentsId]      = useState<number | null>(null);
  const [fileName,               setFileName]               = useState('');
  const [uploading,              setUploading]              = useState(false);

  // ── 從 detail 填入表單 ─────────────────────────────────────────
  const fillForm = (d: SectionCDetail | null) => {
    setProjectStartDate(d?.project_start_date ?? '');
    setProjectDescript(d?.project_descript ?? '');
    setOperationalLifetime(d?.operational_lifetime != null ? String(d.operational_lifetime) : '');
    setCreditingPeriodType(d?.crediting_period_type ?? null);
    setCreditingPeriodStart(d?.crediting_period_start ?? '');
    setCreditingPeriodDuration(d?.crediting_period_duration != null ? String(d.crediting_period_duration) : '');
    setFileAttachmentsId(d?.file_attachments_id ?? null);
    setFileName('');
  };

  // ── 載入資料 ───────────────────────────────────────────────────
  const loadData = useCallback(async (projectId: number) => {
    setLoading(true);
    try {
      await validateSession('SectionCPage');
      const res = await sectionCService.getSectionCByProject(projectId);
      setDetail(res);
      fillForm(res);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedProjectId) { setDetail(null); loadData(selectedProjectId); }
  }, [selectedProjectId, loadData]);

  // ── 檔案上傳 ───────────────────────────────────────────────────
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedProjectId) return;
    setUploading(true);
    try {
      const txn = await requestTransactionToken('file_attachments');
      const res = await fileAttachmentsService.upload({
        file,
        category: 'document',
        business_type: 'section_c',
        related_table: 'section_c_details',
      }, txn.txn_token);
      setFileAttachmentsId(res.file_id);
      setFileName(file.name);
    } catch { show(false, t('sectionC.uploadFailed')); }
    finally { setUploading(false); e.target.value = ''; }
  };

  // ── 儲存 ───────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedProjectId) return;
    setSaving(true);
    try {
      const res = await sectionCService.saveSectionC(selectedProjectId, {
        project_start_date:        projectStartDate || null,
        project_descript:          projectDescript  || null,
        operational_lifetime:      operationalLifetime ? parseInt(operationalLifetime) : null,
        crediting_period_type:     creditingPeriodType,
        crediting_period_start:    creditingPeriodStart || null,
        crediting_period_duration: creditingPeriodDuration ? parseInt(creditingPeriodDuration) : null,
        file_attachments_id:       fileAttachmentsId,
      });
      setDetail(res);
      show(true, t('sectionC.saveSuccess'));
    } catch { show(false, t('sectionC.saveFailed')); }
    finally { setSaving(false); }
  };

  // ── 重置 ───────────────────────────────────────────────────────
  const handleReset = async () => {
    if (!selectedProjectId) return;
    try {
      await sectionCService.resetSectionC(selectedProjectId);
      setDetail(null);
      fillForm(null);
    } catch { /* ignore */ }
    setResetConfirm(false);
  };

  // ── 計入期類型說明文字 ────────────────────────────────────────
  const typeHint = creditingPeriodType === 1
    ? (isZh
        ? 'ℹ 可更新計入期（Renewable）：實際每期年數及可更新期數，依本專案適用方法論之規定，並以東道國核准文件為準，請於 C.3.3 填入實際核准年數並上傳佐證文件。'
        : 'ℹ Renewable crediting period: The duration per period and number of renewals are determined by the applicable methodology and host country approval. Please enter the approved duration in C.3.3 and upload the approval document.')
    : creditingPeriodType === 2
    ? (isZh
        ? 'ℹ 固定計入期（Fixed）：實際年數依本專案適用方法論之規定，並以東道國核准文件為準，請於 C.3.3 填入實際核准年數並上傳佐證文件。'
        : 'ℹ Fixed crediting period: The duration is determined by the applicable methodology and host country approval. Please enter the approved duration in C.3.3 and upload the approval document.')
    : null;

  // ── 日期區間顯示 ──────────────────────────────────────────────
  const durationNum = parseInt(creditingPeriodDuration);
  const dateRangeStr =
    creditingPeriodStart && durationNum > 0
      ? `${creditingPeriodStart} ~ ${calcEndDate(creditingPeriodStart, durationNum)}`
      : '';

  const hasData = !!detail;
  const showReset = canUpdate && hasData;

  const fieldStyle: React.CSSProperties = { marginBottom: 20 };
  const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 };
  const inputStyle: React.CSSProperties = { width: '100%', padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
  const sectionGroupStyle: React.CSSProperties = { background: '#f8faff', border: '1px solid #e0eaff', borderRadius: 8, padding: '16px 20px', marginBottom: 20 };

  return (
    <div className="page-container">
      {/* Page Header */}
      <div className="page-header">
        <FunctionPageHeader funcCode="section_c" />
        {showReset && (
          <div className="page-header-right">
            <button onClick={() => setResetConfirm(true)}
              style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #e53935', background: '#fff', color: '#e53935', cursor: 'pointer', fontWeight: 500, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}
              title={t('sectionC.resetTitle')}>
              🗑 {t('sectionC.resetData')}
            </button>
          </div>
        )}
      </div>

      {/* 未選專案 */}
      {!selectedProjectId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: 16 }}>
          {t('sectionC.selectProject')}
        </div>
      ) : loading ? (
        <div className="loading">{t('sectionC.loading')}</div>
      ) : (
        <div style={{ maxWidth: 800 }}>

          {/* C.1 Project start date */}
          <div style={fieldStyle}>
            <label style={labelStyle}>C.1. {isZh ? '專案開始日期' : 'Project start date'} <span style={{ color: '#4A90E2', fontWeight: 400, fontSize: 12 }}>[project_start_date]</span></label>
            <DateInput value={projectStartDate} onChange={setProjectStartDate} disabled={!canUpdate} />
          </div>

          {/* C.1 東道國核准說明 */}
          <div style={fieldStyle}>
            <label style={labelStyle}>{isZh ? '東道國核准說明' : 'Host Party Approval Description'} <span style={{ color: '#4A90E2', fontWeight: 400, fontSize: 12 }}>[project_descript]</span></label>
            <RichTextEditor value={projectDescript} onChange={setProjectDescript} disabled={!canUpdate} minHeight="96px" />
          </div>

          {/* C.2 Expected operational lifetime */}
          <div style={fieldStyle}>
            <label style={labelStyle}>C.2. {isZh ? '專案活動預期運作年限' : 'Expected operational lifetime of the project'} <span style={{ color: '#4A90E2', fontWeight: 400, fontSize: 12 }}>[operational_lifetime]</span></label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="number" min={1} max={99} value={operationalLifetime}
                onChange={e => setOperationalLifetime(e.target.value)} disabled={!canUpdate}
                placeholder="0" style={{ ...inputStyle, width: 120 }} />
              <span style={{ fontSize: 13, color: '#666' }}>{isZh ? '年' : 'years'}</span>
            </div>
          </div>

          {/* C.3 Project crediting period */}
          <div style={sectionGroupStyle}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16, color: '#333' }}>
              C.3. {isZh ? '專案計入期' : 'Project crediting period'}
            </div>

            {/* C.3.1 Type */}
            <div style={fieldStyle}>
              <label style={labelStyle}>C.3.1. {isZh ? '核准的計入期類別' : 'Type of crediting period approved by the host Party'} <span style={{ color: '#4A90E2', fontWeight: 400, fontSize: 12 }}>[crediting_period_type]</span></label>
              <select value={creditingPeriodType ?? ''} onChange={e => setCreditingPeriodType(e.target.value ? Number(e.target.value) : null)} disabled={!canUpdate}
                style={{ padding: '7px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13, minWidth: 220 }}>
                <option value="">{isZh ? '— 請選擇 —' : '— Select —'}</option>
                <option value={1}>1 — {isZh ? '可更新 [Renewable]' : 'Renewable'}</option>
                <option value={2}>2 — {isZh ? '固定 [Fixed]' : 'Fixed'}</option>
              </select>
              {typeHint && (
                <div style={{ marginTop: 8, padding: '10px 14px', background: '#e8f4ff', border: '1px solid #91caff', borderRadius: 6, fontSize: 12, color: '#0958d9', lineHeight: 1.6 }}>
                  {typeHint}
                </div>
              )}
            </div>

            {/* C.3.2 Start date */}
            <div style={fieldStyle}>
              <label style={labelStyle}>C.3.2. {isZh ? '計入期開始日期' : 'Start date of the crediting period'} <span style={{ color: '#4A90E2', fontWeight: 400, fontSize: 12 }}>[crediting_period_start]</span></label>
              <DateInput value={creditingPeriodStart} onChange={setCreditingPeriodStart} disabled={!canUpdate} />
            </div>

            {/* C.3.3 Duration */}
            <div style={{ marginBottom: 0 }}>
              <label style={labelStyle}>C.3.3. {isZh ? '核准計入期期間' : 'Duration of the crediting period'} <span style={{ color: '#4A90E2', fontWeight: 400, fontSize: 12 }}>[crediting_period_duration]</span></label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <input type="number" min={1} max={99} value={creditingPeriodDuration}
                  onChange={e => setCreditingPeriodDuration(e.target.value)} disabled={!canUpdate}
                  placeholder="0" style={{ ...inputStyle, width: 120 }} />
                <span style={{ fontSize: 13, color: '#666' }}>{isZh ? '年' : 'years'}</span>
              </div>

              {/* 日期區間顯示 */}
              {dateRangeStr && (
                <div style={{ padding: '6px 12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, fontSize: 12, color: '#389e0d', marginBottom: 12 }}>
                  📅 {isZh ? '計入期區間' : 'Crediting period'}：{dateRangeStr}
                </div>
              )}

              {/* 檔案上傳 */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
                  {isZh ? '東道國核准文件' : 'Host Party Approval Document'} <span style={{ color: '#4A90E2', fontWeight: 400, fontSize: 12 }}>[file_attachments_id]</span>
                </div>
                {fileAttachmentsId && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '6px 12px', background: '#f0f8ff', border: '1px solid #b0d0f8', borderRadius: 6, fontSize: 12 }}>
                    <span>📎</span>
                    <span style={{ color: '#0958d9' }}>{fileName || `File #${fileAttachmentsId}`}</span>
                    {canUpdate && (
                      <button onClick={() => { setFileAttachmentsId(null); setFileName(''); }}
                        style={{ marginLeft: 'auto', fontSize: 11, color: '#e53935', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                    )}
                  </div>
                )}
                {canUpdate && (
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 6, border: '1px solid #d0d0d0', background: '#fafafa', cursor: uploading ? 'not-allowed' : 'pointer', fontSize: 12 }}>
                    {uploading ? (isZh ? '上傳中...' : 'Uploading...') : `📤 ${isZh ? '上傳檔案' : 'Upload file'}`}
                    <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleFileChange} disabled={uploading} style={{ display: 'none' }} />
                  </label>
                )}
              </div>
            </div>
          </div>

          {/* 儲存按鈕 */}
          {canUpdate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
              <button onClick={handleSave} disabled={saving}
                style={{ padding: '9px 28px', fontSize: 14, borderRadius: 6, border: 'none', background: saving ? '#ccc' : '#4A90E2', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                {saving ? t('sectionC.saving') : t('sectionC.save')}
              </button>
              {msg && <span style={{ fontSize: 12, color: msg.ok ? '#52c41a' : '#e53935' }}>{msg.text}</span>}
            </div>
          )}
        </div>
      )}

      {/* Reset confirm */}
      {resetConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '28px 32px', minWidth: 360, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{t('sectionC.resetTitle')}</div>
            <div style={{ fontSize: 13, color: '#555', marginBottom: 20, whiteSpace: 'pre-line' }}>{t('sectionC.resetConfirm')}</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setResetConfirm(false)}
                style={{ padding: '6px 18px', borderRadius: 6, border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer' }}>
                {t('sectionC.cancel')}
              </button>
              <button onClick={handleReset}
                style={{ padding: '6px 18px', borderRadius: 6, border: 'none', background: '#ff4d4f', color: '#fff', cursor: 'pointer' }}>
                {t('sectionC.reset')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionCPage;
